import React, { type CSSProperties } from 'react';
import {
    ArrowRight,
    BadgeCheck,
    CalendarDays,
    CheckCircle2,
    Clock3,
    MapPin,
    Navigation,
    Search,
    ShieldCheck,
    Users,
    Zap,
} from 'lucide-react';
import {
    serviceAreasIconTokens,
    serviceAreasTokens,
    type ServiceAreasColorToken,
    type ServiceAreasIconToken,
} from './ServiceAreas.tokens';

export type ServiceAreasLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type ServiceAreasIcon = ServiceAreasIconToken | React.ReactNode | React.ElementType | false;

export type ServiceAreasImageSource =
    | string
    | {
          src?: string;
          width?: number;
          height?: number;
          [key: string]: unknown;
      };

export type ServiceAreasImage = {
    src?: ServiceAreasImageSource;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading?: 'eager' | 'lazy';
    decoding?: 'async' | 'auto' | 'sync';
    decorative?: boolean;
};

export type ServiceAreasFeature = {
    id?: string | number;
    title?: React.ReactNode;
    label?: React.ReactNode;
    description?: React.ReactNode;
    text?: React.ReactNode;
    icon?: ServiceAreasIcon;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
};

export type ServiceAreasMarker = {
    image?: ServiceAreasImage | string | false;
    icon?: ServiceAreasIcon;
    label?: string;
    alt?: string;
};

export type ServiceAreasImageBadge = {
    title?: React.ReactNode;
    description?: React.ReactNode;
    icon?: ServiceAreasIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type ServiceAreasArea = {
    id?: string | number;
    label?: React.ReactNode;
    shortLabel?: React.ReactNode;
    title?: React.ReactNode;
    name?: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    target?: string;
    rel?: string;
    icon?: ServiceAreasIcon;
};

export type ServiceAreasViewAllLink = {
    label?: React.ReactNode;
    href: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
};

export type ServiceAreasFooterNote = {
    text?: React.ReactNode;
    linkLabel?: React.ReactNode;
    href?: string;
    suffix?: React.ReactNode;
    icon?: ServiceAreasIcon;
};

export type ServiceAreasColors = Partial<Record<ServiceAreasColorToken, string>>;

export type ServiceAreasProps = {
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    features?: ServiceAreasFeature[];
    image?: ServiceAreasImage | string | false;
    marker?: ServiceAreasMarker | false;
    imageBadge?: ServiceAreasImageBadge | false;
    areas?: ServiceAreasArea[];
    areasTitle?: React.ReactNode;
    viewAllAreas?: ServiceAreasViewAllLink | false;
    footerNote?: ServiceAreasFooterNote | React.ReactNode | false;
    LinkComponent?: ServiceAreasLinkComponent;
    Link?: ServiceAreasLinkComponent;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4';
    accentColor?: string;
    accentColour?: string;
    accentDarkColor?: string;
    accentDarkColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    actionColor?: string;
    actionColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    textSoftColor?: string;
    textSoftColour?: string;
    colors?: ServiceAreasColors;
    colours?: ServiceAreasColors;
    maxWidth?: number | string;
    id?: string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type ServiceAreasCssVars = CSSProperties &
    Record<`--service-areas-${string}`, string | number | undefined>;

type NormalizedImage = {
    src: string;
    alt: string;
    title?: string;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading: 'eager' | 'lazy';
    decoding: 'async' | 'auto' | 'sync';
    decorative: boolean;
};

type NormalizedFeature = {
    id: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: ServiceAreasIcon;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
    accentSoftColor?: string;
};

type NormalizedArea = {
    id: string | number;
    label: React.ReactNode;
    shortLabel?: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    target?: string;
    rel?: string;
    icon?: ServiceAreasIcon;
};

const t = serviceAreasTokens;
const iconTokenSet = new Set<string>(serviceAreasIconTokens);

const iconRegistry: Record<ServiceAreasIconToken, React.ElementType> = {
    arrowRight: ArrowRight,
    badgeCheck: BadgeCheck,
    calendar: CalendarDays,
    checkCircle: CheckCircle2,
    clock: Clock3,
    mapPin: MapPin,
    navigation: Navigation,
    search: Search,
    shieldCheck: ShieldCheck,
    users: Users,
    zap: Zap,
};

const serviceAreasCss = `
.service-areas {
  --service-areas-bg: ${t.colors.background};
  --service-areas-surface: ${t.colors.surface};
  --service-areas-surface-soft: ${t.colors.surfaceSoft};
  --service-areas-text: ${t.colors.text};
  --service-areas-text-soft: ${t.colors.textSoft};
  --service-areas-text-muted: ${t.colors.textMuted};
  --service-areas-accent: ${t.colors.accent};
  --service-areas-accent-dark: ${t.colors.accentDark};
  --service-areas-accent-soft: ${t.colors.accentSoft};
  --service-areas-accent-wash: ${t.colors.accentWash};
  --service-areas-accent-contrast: ${t.colors.accentContrast};
  --service-areas-action: ${t.colors.action};
  --service-areas-border: ${t.colors.border};
  --service-areas-border-strong: ${t.colors.borderStrong};
  --service-areas-map-overlay: ${t.colors.mapOverlay};
  --service-areas-map-stroke: ${t.colors.mapStroke};
  --service-areas-shadow: ${t.colors.shadow};
  --service-areas-soft-shadow: ${t.colors.softShadow};
  --service-areas-max-width: ${t.sizes.maxWidth}px;
  width: 100%;
  padding: ${t.spacing.sectionY}px ${t.spacing.sectionX}px;
  background: var(--service-areas-bg);
  color: var(--service-areas-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}
.service-areas,
.service-areas * {
  box-sizing: border-box;
}
.service-areas-shell {
  width: 100%;
  max-width: var(--service-areas-max-width);
  margin: 0 auto;
  padding: ${t.spacing.shellPadding}px;
  border: 1px solid var(--service-areas-border);
  border-radius: ${t.radius.shell}px;
  background: var(--service-areas-surface);
  box-shadow: var(--service-areas-shadow);
}
.service-areas-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 0.95fr);
  gap: ${t.spacing.gridGap}px;
  align-items: center;
  width: 100%;
  padding: ${t.spacing.panelPadding}px;
  border: 1px solid var(--service-areas-border-strong);
  border-radius: ${t.radius.panel}px;
  background: var(--service-areas-surface);
}
.service-areas-content {
  width: 100%;
  min-width: 0;
  max-width: ${t.sizes.contentMaxWidth}px;
}
.service-areas-eyebrow {
  display: inline-flex;
  flex-direction: column;
  gap: 10px;
  max-width: 100%;
  margin: 0 0 16px;
  color: var(--service-areas-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
  overflow-wrap: anywhere;
}
.service-areas-eyebrow::after {
  content: "";
  display: block;
  width: 34px;
  height: 2px;
  border-radius: 999px;
  background: currentColor;
}
.service-areas-title {
  max-width: 520px;
  margin: 0;
  color: var(--service-areas-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
  overflow-wrap: anywhere;
}
.service-areas-description {
  max-width: 510px;
  margin: 18px 0 0;
  color: var(--service-areas-text-soft);
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
  overflow-wrap: anywhere;
}
.service-areas-features {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${t.spacing.featureGap}px;
  margin: 26px 0 0;
  padding: 0;
  list-style: none;
}
.service-areas-feature {
  min-width: 0;
  display: grid;
  grid-template-columns: ${t.sizes.icon}px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
}
.service-areas-feature-icon,
.service-areas-note-icon,
.service-areas-badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: ${t.radius.icon}px;
  color: var(--service-areas-item-accent, var(--service-areas-accent));
  background: var(--service-areas-item-soft, var(--service-areas-accent-soft));
}
.service-areas-feature-icon {
  width: ${t.sizes.icon}px;
  height: ${t.sizes.icon}px;
}
.service-areas-feature-icon img,
.service-areas-badge-icon img,
.service-areas-marker-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.service-areas-feature-title {
  min-width: 0;
  margin: 0;
  color: var(--service-areas-text);
  font-size: ${t.typography.featureTitle.fontSize}px;
  line-height: ${t.typography.featureTitle.lineHeight};
  font-weight: ${t.typography.featureTitle.fontWeight};
  letter-spacing: ${t.typography.featureTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.service-areas-feature-description {
  margin: 3px 0 0;
  color: var(--service-areas-text-muted);
  font-size: 12px;
  line-height: 1.35;
  font-weight: 500;
  letter-spacing: 0;
}
.service-areas-map-wrap {
  position: relative;
  width: 100%;
  min-width: 0;
}
.service-areas-map {
  position: relative;
  min-height: ${t.sizes.mapMinHeight}px;
  aspect-ratio: 1.08 / 1;
  overflow: hidden;
  border: 1px solid var(--service-areas-border);
  border-radius: ${t.radius.media}px;
  background:
    linear-gradient(115deg, rgba(222, 244, 232, 0.78), rgba(239, 246, 255, 0.82)),
    var(--service-areas-surface-soft);
}
.service-areas-map-image {
  display: block;
  width: 100%;
  height: 100%;
  min-height: ${t.sizes.mapMinHeight}px;
  object-fit: cover;
}
.service-areas-map-fallback {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background:
    linear-gradient(90deg, rgba(18, 104, 243, 0.08) 1px, transparent 1px),
    linear-gradient(0deg, rgba(18, 104, 243, 0.06) 1px, transparent 1px),
    radial-gradient(circle at 72% 20%, rgba(102, 190, 255, 0.42), transparent 22%),
    radial-gradient(circle at 24% 28%, rgba(162, 229, 184, 0.55), transparent 24%),
    radial-gradient(circle at 58% 72%, rgba(102, 190, 255, 0.34), transparent 24%),
    #eef7f1;
  background-size: 36px 36px, 36px 36px, auto, auto, auto, auto;
}
.service-areas-map-fallback::before {
  content: "";
  position: absolute;
  left: 15%;
  top: 13%;
  width: 68%;
  height: 72%;
  border: 3px solid var(--service-areas-map-stroke);
  border-radius: 38% 50% 44% 46%;
  background: var(--service-areas-map-overlay);
  transform: rotate(-10deg);
}
.service-areas-map-fallback::after {
  content: "Sydney";
  position: absolute;
  left: 58%;
  top: 31%;
  color: rgba(7, 20, 47, 0.7);
  font-size: 16px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
}
.service-areas-marker {
  position: absolute;
  left: 50%;
  top: 46%;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  border: 4px solid var(--service-areas-surface);
  border-radius: 999px;
  background: var(--service-areas-surface);
  color: var(--service-areas-action);
  box-shadow: var(--service-areas-soft-shadow);
  transform: translate(-50%, -50%);
}
.service-areas-marker-image {
  border-radius: 999px;
  padding: 5px;
}
.service-areas-map-badge {
  position: absolute;
  right: 28px;
  bottom: 16px;
  z-index: 3;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  width: min(260px, calc(100% - 32px));
  padding: 14px 16px;
  border: 1px solid var(--service-areas-border);
  border-radius: ${t.radius.panel}px;
  background: rgba(255, 255, 255, 0.94);
  color: var(--service-areas-text);
  box-shadow: var(--service-areas-soft-shadow);
}
.service-areas-badge-icon {
  width: 36px;
  height: 36px;
  color: var(--service-areas-accent-contrast);
  background: var(--service-areas-accent);
}
.service-areas-map-badge-title {
  margin: 0;
  color: var(--service-areas-text);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 850;
  letter-spacing: 0;
}
.service-areas-map-badge-description {
  margin: 3px 0 0;
  color: var(--service-areas-text-soft);
  font-size: 13px;
  line-height: 1.35;
  font-weight: 500;
  letter-spacing: 0;
}
.service-areas-popular {
  margin-top: 18px;
  overflow: hidden;
  border: 1px solid var(--service-areas-border-strong);
  border-radius: ${t.radius.panel}px;
  background: var(--service-areas-surface);
}
.service-areas-popular-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
  padding: 22px 30px 10px;
}
.service-areas-popular-title {
  min-width: 0;
  margin: 0;
  color: var(--service-areas-text);
  font-size: ${t.typography.areaTitle.fontSize}px;
  line-height: ${t.typography.areaTitle.lineHeight};
  font-weight: ${t.typography.areaTitle.fontWeight};
  letter-spacing: ${t.typography.areaTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.service-areas-view-all {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 0 0 auto;
  min-height: 36px;
  color: var(--service-areas-accent);
  font-size: 14px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
  text-decoration: none;
  white-space: nowrap;
}
.service-areas-view-all:hover {
  color: var(--service-areas-accent-dark);
}
.service-areas-view-all:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--service-areas-accent) 24%, transparent);
  outline-offset: 4px;
  border-radius: ${t.radius.button}px;
}
.service-areas-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 30px;
  row-gap: 0;
  margin: 0;
  padding: 8px 30px 26px;
  list-style: none;
}
.service-areas-area {
  min-width: 0;
  border-bottom: 1px solid rgba(7, 20, 47, 0.06);
}
.service-areas-area:nth-last-child(-n + 3) {
  border-bottom: 0;
}
.service-areas-area-link {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 44px;
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--service-areas-text-soft);
  font-size: ${t.typography.areaLink.fontSize}px;
  line-height: ${t.typography.areaLink.lineHeight};
  font-weight: ${t.typography.areaLink.fontWeight};
  letter-spacing: ${t.typography.areaLink.letterSpacing};
  text-align: left;
  text-decoration: none;
}
.service-areas-area-link:hover {
  color: var(--service-areas-accent);
}
.service-areas-area-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--service-areas-accent) 20%, transparent);
  outline-offset: 2px;
  border-radius: ${t.radius.button}px;
}
.service-areas-area-icon,
.service-areas-area-chevron {
  color: var(--service-areas-accent);
}
.service-areas-area-label {
  min-width: 0;
  overflow-wrap: anywhere;
}
.service-areas-area-label-short {
  display: none;
}
.service-areas-footer-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
  padding: 20px 24px;
  border-top: 1px solid var(--service-areas-border);
  background: var(--service-areas-surface-soft);
  color: var(--service-areas-text-soft);
  font-size: 15px;
  line-height: 1.45;
  font-weight: 600;
  letter-spacing: 0;
  text-align: center;
}
.service-areas-note-icon {
  width: 30px;
  height: 30px;
  color: var(--service-areas-accent);
  background: transparent;
}
.service-areas-footer-link {
  color: var(--service-areas-accent);
  font-weight: 850;
  text-decoration: none;
}
.service-areas-footer-link:hover {
  color: var(--service-areas-accent-dark);
}
@media (max-width: 980px) {
  .service-areas {
    padding: 22px 14px;
  }
  .service-areas-shell {
    padding: 14px;
  }
  .service-areas-panel {
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
  }
  .service-areas-content {
    max-width: none;
  }
  .service-areas-title {
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .service-areas-map {
    min-height: ${t.sizes.mapMobileMinHeight}px;
    aspect-ratio: 1.18 / 1;
  }
  .service-areas-map-image {
    min-height: ${t.sizes.mapMobileMinHeight}px;
  }
}
@media (max-width: 720px) {
  .service-areas-shell {
    padding: 0;
    border-radius: ${t.radius.shell}px;
  }
  .service-areas-panel {
    padding: ${t.spacing.panelPaddingMobile}px;
    gap: 14px;
  }
  .service-areas-eyebrow {
    margin-bottom: 14px;
    font-size: ${t.typography.eyebrow.mobileFontSize}px;
  }
  .service-areas-title {
    font-size: ${t.typography.title.mobileFontSize}px;
    line-height: 1.12;
  }
  .service-areas-description {
    margin-top: 14px;
    font-size: ${t.typography.description.mobileFontSize}px;
    line-height: 1.5;
  }
  .service-areas-features {
    gap: 10px;
    margin-top: 22px;
  }
  .service-areas-feature {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    gap: 7px;
    text-align: center;
  }
  .service-areas-feature-icon {
    width: 34px;
    height: 34px;
  }
  .service-areas-feature-title {
    font-size: ${t.typography.featureTitle.mobileFontSize}px;
  }
  .service-areas-feature-description {
    display: none;
  }
  .service-areas-map-badge {
    left: 12px;
    right: 12px;
    bottom: 10px;
    width: auto;
    padding: 12px;
  }
  .service-areas-marker {
    width: 52px;
    height: 52px;
  }
  .service-areas-popular {
    margin-top: 12px;
  }
  .service-areas-popular-header {
    padding: 16px 16px 8px;
  }
  .service-areas-popular-title {
    font-size: ${t.typography.areaTitle.mobileFontSize}px;
  }
  .service-areas-view-all {
    min-height: 32px;
    font-size: 13px;
  }
  .service-areas-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 18px;
    padding: 6px 16px 16px;
  }
  .service-areas-area:nth-last-child(-n + 3) {
    border-bottom: 1px solid rgba(7, 20, 47, 0.06);
  }
  .service-areas-area:nth-last-child(-n + 2) {
    border-bottom: 0;
  }
  .service-areas-area-link {
    grid-template-columns: 18px minmax(0, 1fr) 16px;
    gap: 8px;
    min-height: 34px;
    font-size: ${t.typography.areaLink.mobileFontSize}px;
  }
  .service-areas-area--has-short .service-areas-area-label-full {
    display: none;
  }
  .service-areas-area--has-short .service-areas-area-label-short {
    display: inline;
  }
  .service-areas-footer-note {
    align-items: flex-start;
    padding: 16px;
    font-size: 13px;
  }
}
@media (max-width: 430px) {
  .service-areas {
    padding: 14px 10px;
  }
  .service-areas-panel {
    padding: 14px;
  }
  .service-areas-map {
    min-height: 216px;
  }
  .service-areas-map-image {
    min-height: 216px;
  }
  .service-areas-map-badge-title {
    font-size: 13px;
  }
  .service-areas-map-badge-description {
    font-size: 12px;
  }
}
`;

const joinClassNames = (...classNames: Array<string | false | null | undefined>) =>
    classNames.filter(Boolean).join(' ');

const toCssSize = (value?: number | string) => {
    if (typeof value === 'number') return `${value}px`;
    return value;
};

const kebabCase = (value: string) =>
    value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

function toColorStyles(colors?: ServiceAreasColors): ServiceAreasCssVars {
    if (!colors) return {};

    return Object.entries(colors).reduce<ServiceAreasCssVars>((style, [token, value]) => {
        if (value) {
            style[`--service-areas-${kebabCase(token)}`] = value;
        }

        return style;
    }, {});
}

function isIconToken(icon: unknown): icon is ServiceAreasIconToken {
    return typeof icon === 'string' && iconTokenSet.has(icon);
}

function hasIconContent(icon: ServiceAreasIcon | undefined): boolean {
    return icon !== undefined && icon !== null && icon !== false;
}

function isRenderableIconComponent(icon: ServiceAreasIcon): icon is React.ElementType {
    return (
        typeof icon === 'function' ||
        (typeof icon === 'object' &&
            icon !== null &&
            '$$typeof' in icon &&
            !React.isValidElement(icon))
    );
}

function renderIcon(icon: ServiceAreasIcon | undefined, className: string, size: number) {
    if (!hasIconContent(icon)) return null;

    const iconProps = {
        size,
        strokeWidth: 2.25,
        className,
        'aria-hidden': true,
        focusable: false,
    };

    if (isIconToken(icon)) {
        const Icon = iconRegistry[icon];
        return <Icon {...iconProps} />;
    }

    if (React.isValidElement(icon)) {
        const element = icon as React.ReactElement<any>;
        return React.cloneElement(element, {
            ...iconProps,
            ...(element.props || {}),
        });
    }

    if (isRenderableIconComponent(icon)) {
        const Icon = icon as React.ElementType;
        return <Icon {...iconProps} />;
    }

    return <span className={className}>{icon}</span>;
}

function imageSourceValue(src?: ServiceAreasImage['src']) {
    if (!src) return undefined;
    if (typeof src === 'string') return src;
    return src.src;
}

function normalizeImage(
    image?: ServiceAreasImage | string | false,
    fallbackAlt = '',
): NormalizedImage | undefined {
    if (!image) return undefined;

    if (typeof image === 'string') {
        return {
            src: image,
            alt: fallbackAlt,
            loading: 'lazy',
            decoding: 'async',
            decorative: !fallbackAlt,
        };
    }

    const src = imageSourceValue(image.src) || image.url || image.original || image.thumbnail;

    if (!src) return undefined;

    const alt = image.decorative ? '' : image.alt || fallbackAlt;

    return {
        src,
        alt,
        title: image.title,
        width: image.width || (typeof image.src === 'object' ? image.src.width : undefined),
        height: image.height || (typeof image.src === 'object' ? image.src.height : undefined),
        objectPosition: image.objectPosition,
        loading: image.loading || 'lazy',
        decoding: image.decoding || 'async',
        decorative: image.decorative || !alt,
    };
}

function renderImageIcon(iconSrc?: string, iconAlt?: string) {
    if (!iconSrc) return null;

    return <img src={iconSrc} alt={iconAlt || ''} loading="lazy" decoding="async" />;
}

function normalizeFeatures(features?: ServiceAreasFeature[]): NormalizedFeature[] {
    const source = features && features.length ? features : t.defaults.features;

    return source
        .map((feature, index) => {
            const title = feature.title || feature.label;

            if (!title) return undefined;

            return {
                id: feature.id ?? index,
                title,
                description: feature.description || feature.text,
                icon: feature.icon,
                iconSrc: feature.iconSrc,
                iconAlt: feature.iconAlt,
                accentColor: feature.accentColour || feature.accentColor,
                accentSoftColor: feature.accentSoftColour || feature.accentSoftColor,
            };
        })
        .filter(Boolean) as NormalizedFeature[];
}

function normalizeAreas(areas?: ServiceAreasArea[]): NormalizedArea[] {
    const source = areas && areas.length ? areas : t.defaults.areas;

    return source
        .map((area, index) => {
            const label = area.label || area.title || area.name;

            if (!label) return undefined;

            return {
                id: area.id ?? area.href ?? index,
                label,
                shortLabel: area.shortLabel,
                href: area.href,
                ariaLabel: area.ariaLabel,
                target: area.target,
                rel: area.rel,
                icon: area.icon || 'mapPin',
            };
        })
        .filter(Boolean) as NormalizedArea[];
}

function ServiceAreasLink({
    LinkComponent,
    href,
    className,
    title,
    ariaLabel,
    target,
    rel,
    children,
}: {
    LinkComponent?: ServiceAreasLinkComponent;
    href: string;
    className?: string;
    title?: string;
    ariaLabel?: string;
    target?: string;
    rel?: string;
    children: React.ReactNode;
}) {
    if (LinkComponent) {
        return (
            <LinkComponent
                href={href}
                className={className}
                title={title}
                aria-label={ariaLabel}
                target={target}
                rel={rel}
            >
                {children}
            </LinkComponent>
        );
    }

    return (
        <a
            href={href}
            className={className}
            title={title}
            aria-label={ariaLabel}
            target={target}
            rel={rel}
        >
            {children}
        </a>
    );
}

function FeatureItem({ feature }: { feature: NormalizedFeature }) {
    const style: ServiceAreasCssVars = {
        '--service-areas-item-accent': feature.accentColor || 'var(--service-areas-accent)',
        '--service-areas-item-soft': feature.accentSoftColor || 'var(--service-areas-accent-soft)',
    };

    return (
        <li className="service-areas-feature" style={style}>
            <span className="service-areas-feature-icon">
                {renderImageIcon(feature.iconSrc, feature.iconAlt) ||
                    renderIcon(feature.icon, 'service-areas-icon-node', 18)}
            </span>
            <div>
                <p className="service-areas-feature-title">{feature.title}</p>
                {feature.description ? (
                    <p className="service-areas-feature-description">{feature.description}</p>
                ) : null}
            </div>
        </li>
    );
}

function Marker({ marker }: { marker: ServiceAreasMarker }) {
    const markerImage = normalizeImage(marker.image, marker.alt || marker.label || '');

    return (
        <span
            className="service-areas-marker"
            aria-label={marker.label}
            role={marker.label ? 'img' : undefined}
        >
            {markerImage ? (
                <img
                    className="service-areas-marker-image"
                    src={markerImage.src}
                    alt={markerImage.alt}
                    title={markerImage.title}
                    width={markerImage.width}
                    height={markerImage.height}
                    loading={markerImage.loading}
                    decoding={markerImage.decoding}
                    aria-hidden={markerImage.decorative ? true : undefined}
                />
            ) : (
                renderIcon(marker.icon || 'navigation', 'service-areas-icon-node', 30)
            )}
        </span>
    );
}

function ImageBadge({ badge }: { badge: ServiceAreasImageBadge }) {
    return (
        <div className="service-areas-map-badge">
            <span className="service-areas-badge-icon">
                {renderImageIcon(badge.iconSrc, badge.iconAlt) ||
                    renderIcon(badge.icon || 'checkCircle', 'service-areas-icon-node', 20)}
            </span>
            <div>
                {badge.title ? (
                    <p className="service-areas-map-badge-title">{badge.title}</p>
                ) : null}
                {badge.description ? (
                    <p className="service-areas-map-badge-description">{badge.description}</p>
                ) : null}
            </div>
        </div>
    );
}

function AreaItem({
    area,
    LinkComponent,
}: {
    area: NormalizedArea;
    LinkComponent?: ServiceAreasLinkComponent;
}) {
    const content = (
        <>
            {renderIcon(area.icon, 'service-areas-area-icon', 17)}
            <span className="service-areas-area-label">
                <span className="service-areas-area-label-full">{area.label}</span>
                {area.shortLabel ? (
                    <span className="service-areas-area-label-short">{area.shortLabel}</span>
                ) : null}
            </span>
            {renderIcon('arrowRight', 'service-areas-area-chevron', 15)}
        </>
    );

    return (
        <li
            className={joinClassNames(
                'service-areas-area',
                area.shortLabel && 'service-areas-area--has-short',
            )}
        >
            {area.href ? (
                <ServiceAreasLink
                    LinkComponent={LinkComponent}
                    href={area.href}
                    className="service-areas-area-link"
                    ariaLabel={area.ariaLabel}
                    target={area.target}
                    rel={area.rel}
                >
                    {content}
                </ServiceAreasLink>
            ) : (
                <div className="service-areas-area-link">{content}</div>
            )}
        </li>
    );
}

function isFooterNoteConfig(
    note: ServiceAreasFooterNote | React.ReactNode,
): note is ServiceAreasFooterNote {
    return (
        typeof note === 'object' &&
        note !== null &&
        !Array.isArray(note) &&
        !React.isValidElement(note) &&
        ('text' in note ||
            'linkLabel' in note ||
            'href' in note ||
            'suffix' in note ||
            'icon' in note)
    );
}

function FooterNote({
    note,
    LinkComponent,
}: {
    note: ServiceAreasFooterNote | React.ReactNode;
    LinkComponent?: ServiceAreasLinkComponent;
}) {
    if (!isFooterNoteConfig(note)) {
        return <div className="service-areas-footer-note">{note as React.ReactNode}</div>;
    }

    return (
        <div className="service-areas-footer-note">
            {renderIcon(note.icon || 'mapPin', 'service-areas-note-icon', 20)}
            <span>
                {note.text}
                {note.text && (note.linkLabel || note.suffix) ? ' ' : null}
                {note.href && note.linkLabel ? (
                    <ServiceAreasLink
                        LinkComponent={LinkComponent}
                        href={note.href}
                        className="service-areas-footer-link"
                    >
                        {note.linkLabel}
                    </ServiceAreasLink>
                ) : (
                    note.linkLabel
                )}
                {note.suffix ? <> {note.suffix}</> : null}
            </span>
        </div>
    );
}

export default function ServiceAreas({
    eyebrow = t.defaults.eyebrow,
    title = t.defaults.title,
    description = t.defaults.description,
    features,
    image,
    marker = { icon: 'navigation', label: 'Service area centre' },
    imageBadge = t.defaults.imageBadge,
    areas,
    areasTitle = t.defaults.areasTitle,
    viewAllAreas = { label: t.defaults.viewAllAreasLabel, href: '#service-areas' },
    footerNote = t.defaults.footerNote,
    LinkComponent,
    Link,
    headingLevel = 'h2',
    accentColor,
    accentColour,
    accentDarkColor,
    accentDarkColour,
    accentSoftColor,
    accentSoftColour,
    actionColor,
    actionColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    textSoftColor,
    textSoftColour,
    colors,
    colours,
    maxWidth,
    id,
    className,
    style,
    ariaLabel,
}: ServiceAreasProps) {
    const Heading = headingLevel;
    const resolvedFeatures = normalizeFeatures(features);
    const resolvedAreas = normalizeAreas(areas);
    const resolvedImage = normalizeImage(
        image,
        typeof title === 'string' ? title : 'Service area map',
    );
    const ResolvedLink = LinkComponent || Link;

    const rootStyle: ServiceAreasCssVars = {
        ...toColorStyles(colours || colors),
        '--service-areas-bg':
            backgroundColour || backgroundColor || colours?.background || colors?.background,
        '--service-areas-surface':
            surfaceColour || surfaceColor || colours?.surface || colors?.surface,
        '--service-areas-text': textColour || textColor || colours?.text || colors?.text,
        '--service-areas-text-soft':
            textSoftColour || textSoftColor || colours?.textSoft || colors?.textSoft,
        '--service-areas-accent': accentColour || accentColor || colours?.accent || colors?.accent,
        '--service-areas-accent-dark':
            accentDarkColour || accentDarkColor || colours?.accentDark || colors?.accentDark,
        '--service-areas-accent-soft':
            accentSoftColour || accentSoftColor || colours?.accentSoft || colors?.accentSoft,
        '--service-areas-action': actionColour || actionColor || colours?.action || colors?.action,
        '--service-areas-max-width': toCssSize(maxWidth || t.sizes.maxWidth),
        ...style,
    };

    return (
        <section
            id={id}
            className={joinClassNames('service-areas', className)}
            style={rootStyle}
            aria-label={ariaLabel}
        >
            <style>{serviceAreasCss}</style>
            <div className="service-areas-shell">
                <div className="service-areas-panel">
                    <div className="service-areas-content">
                        {eyebrow ? <p className="service-areas-eyebrow">{eyebrow}</p> : null}
                        {title ? <Heading className="service-areas-title">{title}</Heading> : null}
                        {description ? (
                            <p className="service-areas-description">{description}</p>
                        ) : null}

                        {resolvedFeatures.length ? (
                            <ul className="service-areas-features">
                                {resolvedFeatures.map((feature) => (
                                    <FeatureItem key={feature.id} feature={feature} />
                                ))}
                            </ul>
                        ) : null}
                    </div>

                    <div className="service-areas-map-wrap">
                        <div className="service-areas-map">
                            {resolvedImage ? (
                                <img
                                    className="service-areas-map-image"
                                    src={resolvedImage.src}
                                    alt={resolvedImage.alt}
                                    title={resolvedImage.title}
                                    width={resolvedImage.width}
                                    height={resolvedImage.height}
                                    loading={resolvedImage.loading}
                                    decoding={resolvedImage.decoding}
                                    aria-hidden={resolvedImage.decorative ? true : undefined}
                                    style={
                                        resolvedImage.objectPosition
                                            ? { objectPosition: resolvedImage.objectPosition }
                                            : undefined
                                    }
                                />
                            ) : (
                                <span className="service-areas-map-fallback" aria-hidden="true" />
                            )}
                            {marker ? <Marker marker={marker} /> : null}
                            {imageBadge ? <ImageBadge badge={imageBadge} /> : null}
                        </div>
                    </div>
                </div>

                {resolvedAreas.length ? (
                    <div className="service-areas-popular">
                        <div className="service-areas-popular-header">
                            {areasTitle ? (
                                <h3 className="service-areas-popular-title">{areasTitle}</h3>
                            ) : null}
                            {viewAllAreas ? (
                                <ServiceAreasLink
                                    LinkComponent={ResolvedLink}
                                    href={viewAllAreas.href}
                                    className="service-areas-view-all"
                                    ariaLabel={viewAllAreas.ariaLabel}
                                    title={viewAllAreas.title}
                                    target={viewAllAreas.target}
                                    rel={viewAllAreas.rel}
                                >
                                    <span>
                                        {viewAllAreas.label || t.defaults.viewAllAreasLabel}
                                    </span>
                                    {renderIcon('arrowRight', 'service-areas-icon-node', 16)}
                                </ServiceAreasLink>
                            ) : null}
                        </div>
                        <ul className="service-areas-list">
                            {resolvedAreas.map((area) => (
                                <AreaItem key={area.id} area={area} LinkComponent={ResolvedLink} />
                            ))}
                        </ul>
                        {footerNote ? (
                            <FooterNote note={footerNote} LinkComponent={ResolvedLink} />
                        ) : null}
                    </div>
                ) : null}
            </div>
        </section>
    );
}

export { serviceAreasIconTokens, serviceAreasTokens } from './ServiceAreas.tokens';
export type { ServiceAreasColorToken, ServiceAreasIconToken } from './ServiceAreas.tokens';
